import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";
import {
  Building2,
  Check,
  ExternalLink,
  Loader2,
  Network,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import {
  useAssignOrganizationUser,
  useCreateOrganizationCompany,
  useCreateOrganizationDepartment,
  useDeleteOrganizationDepartment,
  useOrganization,
  useUpdateOrganizationCompany,
  useUpdateOrganizationDepartment,
  type OrganizationDepartment,
} from "../api/organization";

const CARD = "rounded-2xl border border-slate-200/70 bg-white shadow-soft";
const INPUT = "w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100";

function errorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | { message?: string; errors?: Record<string, string[]> }
      | undefined;
    const first = data?.errors ? Object.values(data.errors).flat()[0] : null;
    return first ?? data?.message ?? "No se pudo completar la operación.";
  }

  return "No se pudo completar la operación.";
}

export function OrganizationPage() {
  const { data, isLoading, isFetching, refetch } = useOrganization();
  const createCompany = useCreateOrganizationCompany();
  const updateCompany = useUpdateOrganizationCompany();
  const createDepartment = useCreateOrganizationDepartment();
  const updateDepartment = useUpdateOrganizationDepartment();
  const deleteDepartment = useDeleteOrganizationDepartment();
  const assignUser = useAssignOrganizationUser();

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyCif, setCompanyCif] = useState("");
  const [editingCompany, setEditingCompany] = useState(false);
  const [departmentName, setDepartmentName] = useState("");
  const [editingDepartmentId, setEditingDepartmentId] = useState<number | null>(null);
  const [departmentDraft, setDepartmentDraft] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    destructive?: boolean;
    action: () => void;
  } | null>(null);

  useEffect(() => {
    if (selectedCompanyId !== null || !data?.companies.length) return;

    const inpronet = data.companies.find(
      (company) => company.nombre.trim().toLocaleLowerCase("es") === "inpronet solutions s.l.",
    );
    setSelectedCompanyId(inpronet?.id ?? data.companies[0].id);
  }, [data, selectedCompanyId]);

  const selectedCompany = useMemo(
    () => data?.companies.find((company) => company.id === selectedCompanyId) ?? null,
    [data, selectedCompanyId],
  );

  const visibleUsers = useMemo(() => {
    const query = userSearch.trim().toLocaleLowerCase("es");

    return (data?.users ?? []).filter((user) => {
      if (!showAllUsers && user.emisor_id !== selectedCompanyId) return false;
      if (!query) return true;

      return [user.nombre, user.correo, user.empresa ?? "", user.departamento ?? ""]
        .some((value) => value.toLocaleLowerCase("es").includes(query));
    });
  }, [data, selectedCompanyId, showAllUsers, userSearch]);

  const unassignedCount = useMemo(
    () => (data?.users ?? []).filter((user) => user.emisor_id === null).length,
    [data],
  );

  async function persistCompanyEdit() {
    if (!selectedCompany) return;
    setError("");

    try {
      await updateCompany.mutateAsync({
        id: selectedCompany.id,
        payload: {
          nombre: companyName.trim(),
          cif: companyCif.trim() || null,
        },
      });
      setShowCompanyForm(false);
      setEditingCompany(false);
      setCompanyName("");
      setCompanyCif("");
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  async function submitCompany(event: React.FormEvent) {
    event.preventDefault();
    setError("");

    if (editingCompany && selectedCompany) {
      const nextName = companyName.trim();
      const nextCif = companyCif.trim() || "Sin CIF";
      setConfirmation({
        title: "Confirmar edición de empresa",
        message: `¿Guardar los cambios de "${selectedCompany.nombre}" como "${nextName}" (CIF: ${nextCif})?`,
        confirmLabel: "Guardar cambios",
        action: () => void persistCompanyEdit(),
      });
      return;
    }

    try {
      const response = await createCompany.mutateAsync({
        nombre: companyName.trim(),
        cif: companyCif.trim() || null,
      });
      const createdId = Number(response?.data?.id ?? 0);
      if (createdId > 0) setSelectedCompanyId(createdId);
      setShowCompanyForm(false);
      setEditingCompany(false);
      setCompanyName("");
      setCompanyCif("");
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  function startEditCompany() {
    if (!selectedCompany) return;
    setCompanyName(selectedCompany.nombre);
    setCompanyCif(selectedCompany.cif ?? "");
    setEditingCompany(true);
    setShowCompanyForm(true);
    setError("");
  }

  async function addDepartment(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedCompanyId || !departmentName.trim()) return;
    setError("");

    try {
      await createDepartment.mutateAsync({
        emisor_id: selectedCompanyId,
        nombre: departmentName.trim(),
      });
      setDepartmentName("");
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  async function persistDepartmentEdit(department: OrganizationDepartment, nextName: string) {
    setError("");

    try {
      await updateDepartment.mutateAsync({
        id: department.id,
        payload: { nombre: nextName },
      });
      setEditingDepartmentId(null);
      setDepartmentDraft("");
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  function saveDepartment(department: OrganizationDepartment) {
    const nextName = departmentDraft.trim();
    if (!nextName) return;

    setConfirmation({
      title: "Confirmar edición de departamento",
      message: `¿Cambiar el departamento "${department.nombre}" por "${nextName}"?`,
      confirmLabel: "Guardar cambios",
      action: () => void persistDepartmentEdit(department, nextName),
    });
  }

  async function persistDepartmentStatus(department: OrganizationDepartment) {
    setError("");
    try {
      await updateDepartment.mutateAsync({
        id: department.id,
        payload: { activo: !department.activo },
      });
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  function toggleDepartment(department: OrganizationDepartment) {
    const nextStatus = department.activo ? "inactivo" : "activo";
    setConfirmation({
      title: "Confirmar cambio de estado",
      message: `¿Marcar el departamento "${department.nombre}" como ${nextStatus}?`,
      confirmLabel: department.activo ? "Desactivar" : "Activar",
      action: () => void persistDepartmentStatus(department),
    });
  }

  async function persistDepartmentRemoval(department: OrganizationDepartment) {
    setError("");

    try {
      await deleteDepartment.mutateAsync(department.id);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  function removeDepartment(department: OrganizationDepartment) {
    setConfirmation({
      title: "Eliminar departamento",
      message: `¿Eliminar definitivamente el departamento "${department.nombre}"? Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      destructive: true,
      action: () => void persistDepartmentRemoval(department),
    });
  }

  async function changeUserCompany(userId: number, companyId: number | null) {
    setError("");
    try {
      await assignUser.mutateAsync({
        id: userId,
        emisor_id: companyId,
        departamento_id: null,
      });
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  async function changeUserDepartment(userId: number, companyId: number | null, departmentId: number | null) {
    setError("");
    try {
      await assignUser.mutateAsync({
        id: userId,
        emisor_id: companyId,
        departamento_id: departmentId,
      });
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  if (isLoading) {
    return (
      <div className="p-8 text-slate-500 flex items-center gap-2">
        <Loader2 size={18} className="animate-spin" /> Cargando empresas y departamentos…
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-auto px-4 py-5 sm:px-5 sm:py-6 lg:px-6 xl:px-8">
      <div className="flex w-full min-w-0 flex-col gap-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-3 text-2xl font-black text-slate-900 sm:text-3xl">
              <Network size={28} className="text-brand-700" /> Empresas y departamentos
            </h1>
            <p className="mt-1 max-w-3xl text-sm font-semibold text-slate-500">
              Gestiona la relación entre empresas, departamentos y usuarios desde una única vista.
            </p>
          </div>

          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw size={15} className={isFetching ? "animate-spin" : ""} /> Actualizar
            </button>
            <Link
              to="/billing/issuers"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-600 hover:border-brand-300 hover:text-brand-700"
            >
              <ExternalLink size={15} /> Gestión completa de emisores
            </Link>
          </div>
        </header>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        )}

        <section className={`${CARD} p-4 sm:p-5`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-black text-slate-900">
                <Building2 size={19} className="text-brand-700" /> Empresas
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Selecciona una empresa para gestionar sus departamentos y revisar su equipo.
              </p>
            </div>
            <div className="flex w-full flex-wrap gap-2 sm:w-auto">
              {selectedCompany && (
                <button
                  type="button"
                  onClick={startEditCompany}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
                >
                  <Pencil size={14} /> Editar empresa
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setEditingCompany(false);
                  setCompanyName("");
                  setCompanyCif("");
                  setShowCompanyForm(true);
                  setError("");
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-brand-700 px-3 py-2 text-sm font-black text-white hover:bg-brand-800"
              >
                <Plus size={15} /> Nueva empresa
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {(data?.companies ?? []).map((company) => (
              <button
                key={company.id}
                type="button"
                onClick={() => {
                  setSelectedCompanyId(company.id);
                  setShowCompanyForm(false);
                  setEditingCompany(false);
                }}
                className={`rounded-2xl border p-4 text-left transition ${
                  company.id === selectedCompanyId
                    ? "border-brand-300 bg-brand-50/70 ring-2 ring-brand-100"
                    : "border-slate-200 bg-white hover:border-brand-200 hover:bg-slate-50"
                }`}
              >
                <div className="font-black text-slate-900">{company.nombre}</div>
                <div className="mt-1 text-xs font-semibold text-slate-500">{company.cif || "Sin CIF"}</div>
                <div className="mt-3 flex gap-3 text-xs font-bold text-slate-500">
                  <span>{company.departments.length} departamentos</span>
                  <span>{company.users_count} usuarios</span>
                </div>
              </button>
            ))}
          </div>

          {showCompanyForm && (
            <form onSubmit={submitCompany} className="mt-4 grid min-w-0 gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[minmax(0,1fr)_minmax(180px,260px)_auto]">
              <input
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                placeholder="Nombre de la empresa"
                className={INPUT}
                required
              />
              <input
                value={companyCif}
                onChange={(event) => setCompanyCif(event.target.value)}
                placeholder="CIF (opcional)"
                className={INPUT}
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={!companyName.trim() || createCompany.isPending || updateCompany.isPending}
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-700 px-4 py-2 text-sm font-black text-white disabled:opacity-50"
                >
                  {(createCompany.isPending || updateCompany.isPending) ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {editingCompany ? "Guardar" : "Crear"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCompanyForm(false);
                    setEditingCompany(false);
                  }}
                  className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500"
                  aria-label="Cancelar"
                >
                  <X size={16} />
                </button>
              </div>
            </form>
          )}
        </section>

        {selectedCompany && (
          <section className={`${CARD} p-4 sm:p-5`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-black text-slate-900">Departamentos · {selectedCompany.nombre}</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Los departamentos inactivos permanecen visibles, pero no se ofrecen para nuevas asignaciones.
                </p>
              </div>
              <form onSubmit={addDepartment} className="grid w-full min-w-0 gap-2 sm:w-auto sm:min-w-[320px] sm:grid-cols-[minmax(200px,1fr)_auto]">
                <input
                  value={departmentName}
                  onChange={(event) => setDepartmentName(event.target.value)}
                  placeholder="Nuevo departamento"
                  className={INPUT}
                />
                <button
                  type="submit"
                  disabled={!departmentName.trim() || createDepartment.isPending}
                  className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-brand-700 px-3 py-2 text-sm font-black text-white disabled:opacity-50"
                >
                  <Plus size={14} /> Añadir
                </button>
              </form>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {selectedCompany.departments.map((department) => (
                <div key={department.id} className="rounded-2xl border border-slate-200 p-4">
                  {editingDepartmentId === department.id ? (
                    <div className="flex gap-2">
                      <input
                        value={departmentDraft}
                        onChange={(event) => setDepartmentDraft(event.target.value)}
                        className={INPUT}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => saveDepartment(department)}
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-700 text-white"
                        aria-label="Guardar departamento"
                      >
                        <Check size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingDepartmentId(null)}
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-500"
                        aria-label="Cancelar edición"
                      >
                        <X size={15} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className={`truncate font-black ${department.activo ? "text-slate-900" : "text-slate-400 line-through"}`}>
                          {department.nombre}
                        </div>
                        <div className="mt-1 text-xs font-semibold text-slate-500">
                          {department.users_count} usuario(s)
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => toggleDepartment(department)}
                          className={`rounded-lg border px-2 py-1 text-xs font-bold ${
                            department.activo
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 bg-slate-100 text-slate-500"
                          }`}
                        >
                          {department.activo ? "Activo" : "Inactivo"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingDepartmentId(department.id);
                            setDepartmentDraft(department.nombre);
                          }}
                          className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                          aria-label="Editar departamento"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeDepartment(department)}
                          className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                          aria-label="Eliminar departamento"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {selectedCompany.departments.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
                  Esta empresa todavía no tiene departamentos.
                </div>
              )}
            </div>
          </section>
        )}

        <section className={`${CARD} min-h-[420px] overflow-hidden`}>
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4 sm:p-5">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-black text-slate-900">
                <Users size={18} className="text-brand-700" /> Usuarios
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {showAllUsers
                  ? `${visibleUsers.length} usuario(s) visibles en todas las empresas.`
                  : `${visibleUsers.length} usuario(s) pertenecen a ${selectedCompany?.nombre ?? "la empresa seleccionada"}.`}
                {unassignedCount > 0 ? ` ${unassignedCount} sin empresa asignada.` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="flex w-full min-w-0 items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-500 sm:w-auto sm:min-w-[260px]">
                <Search size={15} />
                <input
                  value={userSearch}
                  onChange={(event) => setUserSearch(event.target.value)}
                  placeholder="Buscar usuario…"
                  className="w-full bg-transparent outline-none"
                />
              </label>
              <button
                type="button"
                onClick={() => setShowAllUsers((value) => !value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
              >
                {showAllUsers ? "Solo empresa seleccionada" : "Ver todos"}
              </button>
            </div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-[940px] w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3">Usuario</th>
                  <th className="px-5 py-3">Empresa</th>
                  <th className="px-5 py-3">Departamento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleUsers.map((user) => {
                  const company = data?.companies.find((item) => item.id === user.emisor_id) ?? null;
                  const availableDepartments = company?.departments.filter((department) => department.activo) ?? [];
                  const currentDepartmentMissing = user.departamento_id !== null
                    && !availableDepartments.some((department) => department.id === user.departamento_id);

                  return (
                    <tr key={user.id} className="hover:bg-slate-50/70">
                      <td className="px-5 py-3">
                        <div className="font-bold text-slate-900">{user.nombre}</div>
                        <div className="text-xs text-slate-500">{user.correo}</div>
                      </td>
                      <td className="px-5 py-3">
                        <select
                          value={user.emisor_id ?? ""}
                          onChange={(event) => void changeUserCompany(user.id, event.target.value ? Number(event.target.value) : null)}
                          disabled={assignUser.isPending}
                          className={INPUT}
                        >
                          <option value="">Sin empresa</option>
                          {(data?.companies ?? []).map((item) => (
                            <option key={item.id} value={item.id}>{item.nombre}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3">
                        <select
                          value={user.departamento_id ?? ""}
                          onChange={(event) => void changeUserDepartment(
                            user.id,
                            user.emisor_id,
                            event.target.value ? Number(event.target.value) : null,
                          )}
                          disabled={user.emisor_id === null || assignUser.isPending}
                          className={INPUT}
                        >
                          <option value="">Sin departamento</option>
                          {availableDepartments.map((department) => (
                            <option key={department.id} value={department.id}>{department.nombre}</option>
                          ))}
                          {currentDepartmentMissing && user.departamento_id !== null && (
                            <option value={user.departamento_id}>{user.departamento ?? "Departamento antiguo"} (inactivo)</option>
                          )}
                        </select>
                      </td>
                    </tr>
                  );
                })}
                {visibleUsers.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-5 py-12 text-center text-sm text-slate-400">
                      No hay usuarios que coincidan con el filtro actual.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={confirmation !== null}
        title={confirmation?.title ?? "Confirmar acción"}
        message={confirmation?.message}
        confirmLabel={confirmation?.confirmLabel}
        destructive={confirmation?.destructive}
        onClose={() => setConfirmation(null)}
        onConfirm={() => confirmation?.action()}
      />
    </div>
  );
}
